import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import VatTotals from '../common/VatTotals';
import { fmtMoney, todayIso } from '../../utils/format';

const VAT_RATE = 21;
const round2 = (n) => Math.round(n * 100) / 100;
const emptyRow = (description = '') => ({ key: Math.random(), description, quantity: '1', unit: '', unitPrice: '' });
const lineTotal = (r) => (r.quantity !== '' && r.unitPrice !== '' && !Number.isNaN(Number(r.quantity) * Number(r.unitPrice))
    ? round2(Number(r.quantity) * Number(r.unitPrice)) : null);

/**
 * Create or edit a pro-forma invoice: any positions with prices typed by hand (without PDV), PDV 21% on top
 * by default (switch it off for "without PDV"), date, optional due date and note. A new one starts with one
 * position named after the job. `onSave(payload)` saves; the dialog stays open if it throws.
 */
export default function ProformaDialog({ open, proforma, job, onClose, onSave }) {
    const { t } = useTranslation();
    const phone = useMediaQuery((th) => th.breakpoints.down('sm'));
    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setForm(proforma ? {
            issueDate: proforma.issueDate, dueDate: proforma.dueDate ?? '', note: proforma.note ?? '',
            vatEnabled: proforma.vatEnabled, vatRate: proforma.vatRate,
            rows: proforma.items.map(i => ({ key: i.id, description: i.description, quantity: String(i.quantity), unit: i.unit ?? '', unitPrice: String(i.unitPrice) })),
        } : {
            issueDate: todayIso(), dueDate: '', note: '', vatEnabled: true, vatRate: VAT_RATE, rows: [emptyRow(job?.title ?? '')],
        });
    }, [open, proforma, job]);

    if (!form) return null;

    const setRow = (key, k) => (e) => {
        const v = e.target.value;
        setForm(f => ({ ...f, rows: f.rows.map(r => (r.key === key ? { ...r, [k]: v } : r)) }));
        if (errors[`${key}.${k}`]) setErrors(x => ({ ...x, [`${key}.${k}`]: undefined }));
    };
    const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); if (errors[k]) setErrors(x => ({ ...x, [k]: undefined })); };

    const net = round2(form.rows.reduce((sum, r) => sum + (lineTotal(r) ?? 0), 0));
    const vatAmount = form.vatEnabled ? round2((net * Number(form.vatRate)) / 100) : 0;
    const doc = { net, vatAmount, total: round2(net + vatAmount), vatEnabled: form.vatEnabled, vatRate: form.vatRate };

    const save = async () => {
        const v = {};
        if (!form.issueDate) v.issueDate = t('validation.required');
        if (form.dueDate && form.issueDate && form.dueDate < form.issueDate) v.dueDate = t('errors.DUE_BEFORE_ISSUE');
        form.rows.forEach(r => {
            if (!r.description.trim()) v[`${r.key}.description`] = t('validation.required');
            if (!(Number(r.quantity) > 0)) v[`${r.key}.quantity`] = t('proforma.errors.quantity');
            if (r.unitPrice === '' || Number(r.unitPrice) < 0 || Number.isNaN(Number(r.unitPrice))) v[`${r.key}.unitPrice`] = t('proforma.errors.price');
        });
        setErrors(v);
        if (Object.keys(v).length) return;
        setBusy(true);
        try {
            await onSave({
                issueDate: form.issueDate, dueDate: form.dueDate || null, note: form.note.trim() || null,
                vatEnabled: form.vatEnabled, vatRate: Number(form.vatRate),
                items: form.rows.map(r => ({ description: r.description.trim(), quantity: Number(r.quantity), unit: r.unit.trim() || null, unitPrice: Number(r.unitPrice) })),
            });
        } catch {
            // The caller shows the error; keep the dialog open with what was typed.
        } finally {
            setBusy(false);
        }
    };

    const err = (key, k) => errors[`${key}.${k}`];

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth fullScreen={phone}
                PaperProps={{ sx: { borderRadius: phone ? 0 : '16px' } }}>
            <DialogTitle sx={{ fontWeight: 700 }}>
                {proforma ? t('proforma.editTitle', { number: proforma.number }) : t('proforma.newTitle')}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
                    <Grid size={{ xs: 6, sm: 4 }}>
                        <TextField fullWidth size="small" type="date" required label={t('proforma.issueDate')} value={form.issueDate} onChange={set('issueDate')}
                                   error={!!errors.issueDate} helperText={errors.issueDate} slotProps={{ inputLabel: { shrink: true } }} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                        <TextField fullWidth size="small" type="date" label={t('proforma.dueDate')} value={form.dueDate} onChange={set('dueDate')}
                                   error={!!errors.dueDate} helperText={errors.dueDate} slotProps={{ inputLabel: { shrink: true } }} />
                    </Grid>
                </Grid>

                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5 }}>
                    {t('proforma.positions')}
                </Typography>
                {form.rows.map((r, idx) => (
                    <Grid container spacing={1.5} key={r.key} sx={{ mb: 2, pb: { xs: 2, sm: 0 }, borderBottom: { xs: '1px solid #F1F5F9', sm: 0 } }}>
                        <Grid size={{ xs: 12, sm: 5 }}>
                            <TextField fullWidth size="small" multiline maxRows={4} label={`${idx + 1}. ${t('quotes.items.description')}`}
                                       value={r.description} onChange={setRow(r.key, 'description')}
                                       error={!!err(r.key, 'description')} helperText={err(r.key, 'description')}
                                       slotProps={{ htmlInput: { maxLength: 500 } }} />
                        </Grid>
                        <Grid size={{ xs: 3, sm: 1.5 }}>
                            <TextField fullWidth size="small" type="number" label={t('quotes.items.quantity')} value={r.quantity} onChange={setRow(r.key, 'quantity')}
                                       error={!!err(r.key, 'quantity')} helperText={err(r.key, 'quantity')} slotProps={{ htmlInput: { min: 0, step: 'any' } }} />
                        </Grid>
                        <Grid size={{ xs: 3, sm: 1.5 }}>
                            <TextField fullWidth size="small" label={t('quotes.items.unit')} value={r.unit} onChange={setRow(r.key, 'unit')}
                                       slotProps={{ htmlInput: { maxLength: 20 } }} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 2 }}>
                            <TextField fullWidth size="small" type="number" label={t('quotes.items.unitPrice')} value={r.unitPrice} onChange={setRow(r.key, 'unitPrice')}
                                       error={!!err(r.key, 'unitPrice')} helperText={err(r.key, 'unitPrice')} slotProps={{ htmlInput: { min: 0, step: 'any' } }} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, minHeight: 40 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap' }}>{fmtMoney(lineTotal(r))}</Typography>
                            {/* Always visible (touch screens); a pro-forma needs at least one position. */}
                            <Tooltip title={t('common.delete')}>
                                <span>
                                    <IconButton size="small" disabled={form.rows.length === 1} aria-label={t('common.delete')}
                                                onClick={() => setForm(f => ({ ...f, rows: f.rows.filter(x => x.key !== r.key) }))}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                ))}
                <Button size="small" startIcon={<AddIcon />} disabled={form.rows.length >= 100}
                        onClick={() => setForm(f => ({ ...f, rows: [...f.rows, emptyRow()] }))}>
                    {t('proforma.addPosition')}
                </Button>

                <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #E2E8F0' }}>
                    <VatTotals doc={doc} onToggle={(on) => setForm(f => ({ ...f, vatEnabled: on }))} />
                </Box>

                <TextField fullWidth size="small" multiline minRows={2} label={t('proforma.note')} placeholder={t('proforma.notePlaceholder')}
                           value={form.note} onChange={set('note')} sx={{ mt: 3 }} slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 2000 } }} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={busy} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" disableElevation onClick={save} disabled={busy} sx={{ minWidth: 120 }}>
                    {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
